<script setup lang="ts">
import { MaintenanceOutage } from '../services/maintenance-outage';
import { computed } from 'vue';
import { useLocalizedDateHelpers } from '../services/date.ts';
import MaintenanceOutageList from '../components/maintenance-outage/MaintenanceOutageList.vue';
import { AppPageProps, GuestPageProps, Pagination } from '../index';
import { usePage } from '@inertiajs/vue3';
import AppLayout from '../layouts/AppLayout.vue';
import GuestLayout from '../layouts/GuestLayout.vue';
import { MaintenanceOutageType } from '../services/maintenance-outage/maintenance-outage-type.enum.ts';

const props = defineProps<{
  maintenanceOutages: Pagination<MaintenanceOutage>;
}>();

const page = usePage<AppPageProps | GuestPageProps>();

const { formatDate } = useLocalizedDateHelpers();

const currentMaintenanceOutages = computed(() => {
  return props.maintenanceOutages.data.filter(
    (maintenanceOutage) =>
      (maintenanceOutage.type === MaintenanceOutageType.outage ||
        new Date(maintenanceOutage.start_at) <= new Date()) &&
      (!maintenanceOutage.end_at || new Date(maintenanceOutage.end_at) > new Date()),
  );
});

// Group maintenance outages by date in descending order
const groupedByDate = computed(() => {
  const maintenanceOutages = props.maintenanceOutages.data.filter((maintenanceOutage) =>
    currentMaintenanceOutages.value.every(
      (activeOutage) => activeOutage.id !== maintenanceOutage.id,
    ),
  );

  const grouped = new Map<string, MaintenanceOutage[]>();
  for (const outage of maintenanceOutages) {
    const date = new Date(outage.start_at).toDateString();
    if (!grouped.has(date)) {
      grouped.set(date, []);
    }
    grouped.get(date)?.push(outage);
  }
  return grouped;
});

const layout = computed(() => (page.props.auth.user ? AppLayout : GuestLayout));
</script>

<template>
  <component :is="layout" :title="$t('maintenanceOutage.title')">
    <template v-if="maintenanceOutages.total === 0">
      <VCard :title="$t('maintenanceOutage.none')" />
    </template>
    <template v-else>
      <VCard :title="$t('maintenanceOutage.current')">
        <MaintenanceOutageList :maintenance-outages="currentMaintenanceOutages" />
      </VCard>
      <VCard
        v-for="[date, maintenanceOutages] in groupedByDate"
        :key="date"
        :title="formatDate(date, { dateStyle: 'full' })"
        class="mt-8"
      >
        <MaintenanceOutageList :maintenance-outages="maintenanceOutages" />
      </VCard>
    </template>
  </component>
</template>
